import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import PageHeader from './layout/PageHeader';
import {
  CloudUpload,
  Download,
  Visibility,
  CheckCircle,
  Error,
  TableChart
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useHomeExpenseImportExport } from '../hooks/useHomeExpenseImportExport';
import { ExpenseType } from '../types/homeExpenses';
import { formatNumber } from '../utils/currency';
import { formatDate } from '../utils/date';

interface ImportValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  totalRows: number;
  detectedHeaders: string[];
  previewRecords: ImportedRecord[];
}

interface ImportedRecord {
  rowNumber: number;
  date: string;
  group: string;
  amount: number;
  account: string;
  observations?: string;
  isSuccess: boolean;
  errorMessage?: string;
}

interface ImportResult {
  success: boolean;
  message: string;
  totalRecords: number;
  successfulImports: number;
  failedImports: number;
  errors: string[];
  warnings: string[];
  importedRecords: ImportedRecord[];
}

interface ExportOptions {
  startDate?: string;
  endDate?: string;
  groups: string[];
  accounts: string[];
  includeIncome: boolean;
  includeExpenses: boolean;
  format: 'Csv' | 'Xlsx';
  type?: ExpenseType;
}

const HomeExpenseImportExport: React.FC = () => {
  const location = useLocation();
  
  // Detect expense type from route
  const expenseType = location.pathname.includes('personal') 
    ? ExpenseType.Personal 
    : ExpenseType.Home;
  
  const {
    loading,
    error: hookError,
    validateFile: validateFileHook,
    importFile: importFileHook,
    exportData: exportDataHook,
    downloadTemplate: downloadTemplateHook,
    downloadFile,
    clearError
  } = useHomeExpenseImportExport();

  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [clearExistingData, setClearExistingData] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    groups: [],
    accounts: [],
    includeIncome: true,
    includeExpenses: true,
    format: 'Csv',
    type: expenseType
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationResult(null);
    setImportResult(null);

    // Validate file
    await validateFile(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1
  });

  const validateFile = async (file: File) => {
    clearError();
    const result = await validateFileHook(file);
    if (result) {
      setValidationResult(result);
      setShowPreview(true);
    } else {
      setValidationResult({
        isValid: false,
        errors: [hookError || 'A validação falhou'],
        warnings: [],
        totalRows: 0,
        detectedHeaders: [],
        previewRecords: []
      });
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !validationResult?.isValid) return;

    clearError();
    const result = await importFileHook(selectedFile, clearExistingData);
    if (result) {
      setImportResult(result);
      setShowPreview(false);
    } else {
      setImportResult({
        success: false,
        message: hookError || 'A importação falhou',
        totalRecords: 0,
        successfulImports: 0,
        failedImports: 0,
        errors: [hookError || 'A importação falhou'],
        warnings: [],
        importedRecords: []
      });
    }
  };

  const handleExport = async () => {
    clearError();
    const blob = await exportDataHook(exportOptions);
    if (blob) {
      const fileName = `home_expenses_${new Date().toISOString().split('T')[0]}.${exportOptions.format.toLowerCase()}`;
      downloadFile(blob, fileName);
    }
  };

  const downloadTemplate = async (format: 'Csv' | 'Xlsx') => {
    clearError();
    const blob = await downloadTemplateHook(format);
    if (blob) {
      downloadFile(blob, `home_expenses_template.${format.toLowerCase()}`);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setImportResult(null);
    setShowPreview(false);
  };

  return (
    <Box>
      <PageHeader title={`Importar/Exportar despesas ${expenseType === ExpenseType.Home ? 'da casa' : 'pessoais'}`} />

      <Grid container spacing={3}>
        {/* Import Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Importar despesas
              </Typography>

              {/* Templates */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Descarregar modelos:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<TableChart />}
                    onClick={() => downloadTemplate('Csv')}
                  >
                    Modelo CSV
                  </Button>
                  <Button
                    size="small"
                    startIcon={<TableChart />}
                    onClick={() => downloadTemplate('Xlsx')}
                  >
                    Modelo Excel
                  </Button>
                </Box>
              </Box>

              {/* File Upload */}
              <Paper
                {...getRootProps()}
                sx={{
                  p: 3,
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                  cursor: 'pointer',
                  textAlign: 'center',
                  mb: 2
                }}
              >
                <input {...getInputProps()} />
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {isDragActive
                    ? 'Largue o ficheiro aqui...'
                    : 'Arraste um ficheiro CSV ou Excel para aqui, ou clique para selecionar'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Formatos suportados: .csv, .xlsx
                </Typography>
              </Paper>

              {selectedFile && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Ficheiro selecionado: {selectedFile.name}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={clearExistingData}
                        onChange={(e) => setClearExistingData(e.target.checked)}
                      />
                    }
                    label="Apagar as despesas existentes antes de importar"
                  />
                </Box>
              )}

              {loading && <LinearProgress sx={{ mb: 2 }} />}

              {/* Validation Results */}
              {validationResult && (
                <Box sx={{ mb: 2 }}>
                  {!validationResult.isValid ? (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      A validação falhou
                    </Alert>
                  ) : (
                    <Alert severity="success" sx={{ mb: 1 }}>
                      Ficheiro validado com sucesso
                    </Alert>
                  )}

                  {validationResult.errors.map((error, index) => (
                    <Alert key={index} severity="error" sx={{ mb: 1 }}>
                      {error}
                    </Alert>
                  ))}

                  {validationResult.warnings.map((warning, index) => (
                    <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                      {warning}
                    </Alert>
                  ))}

                  {validationResult.isValid && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={loading}
                      >
                        Importar {validationResult.totalRows} registos
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => setShowPreview(true)}
                      >
                        Pré-visualizar
                      </Button>
                      <Button onClick={resetImport}>Repor</Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Import Results */}
              {importResult && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity={importResult.success ? 'success' : 'error'} sx={{ mb: 1 }}>
                    {importResult.message}
                  </Alert>

                  {importResult.success && (
                    <Typography variant="body2">
                      Importados com sucesso {importResult.successfulImports} de {importResult.totalRecords} registos
                    </Typography>
                  )}

                  {importResult.errors.map((error, index) => (
                    <Alert key={index} severity="error" sx={{ mb: 1 }}>
                      {error}
                    </Alert>
                  ))}

                  <Button onClick={resetImport} sx={{ mt: 1 }}>
                    Importar outro ficheiro
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Export Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Exportar despesas
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Formato:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant={exportOptions.format === 'Csv' ? 'contained' : 'outlined'}
                    onClick={() => setExportOptions({ ...exportOptions, format: 'Csv', type: expenseType })}
                  >
                    CSV
                  </Button>
                  <Button
                    variant={exportOptions.format === 'Xlsx' ? 'contained' : 'outlined'}
                    onClick={() => setExportOptions({ ...exportOptions, format: 'Xlsx', type: expenseType })}
                  >
                    Excel
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeIncome}
                      onChange={(e) => setExportOptions({ 
                        ...exportOptions, 
                        includeIncome: e.target.checked 
                      })}
                    />
                  }
                  label="Incluir receitas"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportOptions.includeExpenses}
                      onChange={(e) => setExportOptions({ 
                        ...exportOptions, 
                        includeExpenses: e.target.checked 
                      })}
                    />
                  }
                  label="Incluir despesas"
                />
              </Box>

              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleExport}
                disabled={loading}
                fullWidth
              >
                Exportar dados
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Pré-visualização da importação
          {validationResult && (
            <Typography variant="body2" color="text.secondary">
              {validationResult.totalRows} registos detetados
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {validationResult && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Cabeçalhos detetados:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {validationResult.detectedHeaders.map((header, index) => (
                    <Chip key={index} label={header} size="small" />
                  ))}
                </Box>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Linha</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Grupo</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Conta</TableCell>
                      <TableCell>Observações</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResult.previewRecords.map((record) => (
                      <TableRow key={record.rowNumber}>
                        <TableCell>{record.rowNumber}</TableCell>
                        <TableCell>{formatDate(record.date) || record.date}</TableCell>
                        <TableCell>{record.group}</TableCell>
                        <TableCell>{formatNumber(record.amount)}</TableCell>
                        <TableCell>{record.account}</TableCell>
                        <TableCell>{record.observations}</TableCell>
                        <TableCell>
                          {record.isSuccess ? (
                            <Chip
                              icon={<CheckCircle />}
                              label="Válido"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Tooltip title={record.errorMessage}>
                              <Chip
                                icon={<Error />}
                                label="Erro"
                                color="error"
                                size="small"
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Fechar</Button>
          {validationResult?.isValid && (
            <Button variant="contained" onClick={handleImport}>
              Importar dados
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeExpenseImportExport;
